import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
    Receipt,
    ArrowLeft,
    Save,
    Calendar,
    Building,
    Loader2,
    AlertCircle,
    Plus,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { payoutService } from '@/services/payoutService'
import { CreatePayoutBatchData, UpdatePayoutBatchData } from '@/types'

export default function AdminPayoutBatchForm() {
    const { branding } = useBranding()
    const pageStyles = getPageStyles(branding)
    const cardStyles = getCardStyles(branding)
    const statusColors = getStatusColors(branding)
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEdit = Boolean(id)

    const [formData, setFormData] = useState<CreatePayoutBatchData>({
        merchant: 0,
        submerchant: undefined,
        period_start: '',
        period_end: '',
        currency: 'USD',
    })

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [merchants, setMerchants] = useState<any[]>([])
    const [dateError, setDateError] = useState<string | null>(null)

    useEffect(() => {
        if (isEdit && id) {
            fetchBatchData()
        }
        fetchMerchants()
    }, [id, isEdit])

    const fetchBatchData = async () => {
        if (!id) return

        try {
            setLoading(true)
            const response = await payoutService.getPayoutBatch(parseInt(id))

            if (response.success) {
                const batch = response.data
                setFormData({
                    merchant: batch.merchant,
                    submerchant: batch.submerchant || undefined,
                    period_start: batch.period_start.split('T')[0], // Convert to date input format
                    period_end: batch.period_end.split('T')[0],
                    currency: batch.currency,
                })
            } else {
                setError('Failed to fetch payout batch')
            }
        } catch (err) {
            setError('An error occurred while fetching batch data')
            console.error('Error fetching batch data:', err)
        } finally {
            setLoading(false)
        }
    }

    const fetchMerchants = async () => {
        try {
            const response = await payoutService.getMerchants()
            if (response.success) {
                console.log('Raw merchants response:', response.data)
                console.log('Merchants results:', response.data.results)
                // Filter to only show enabled merchants
                const enabledMerchants = response.data.results.filter((merchant: any) => merchant.status === 'enabled')
                console.log('Enabled merchants:', enabledMerchants)
                setMerchants(enabledMerchants)

                if (enabledMerchants.length === 0) {
                    console.warn('No enabled merchants found!')
                }
            }
        } catch (err) {
            console.error('Error fetching merchants:', err)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.merchant || formData.merchant === 0 || !formData.period_start || !formData.period_end) {
            setError('Please fill in all required fields')
            return
        }

        if (merchants.length === 0) {
            setError('No enabled merchants available. Please contact an administrator.')
            return
        }

        // Validate that period start is before period end
        const startDate = new Date(formData.period_start)
        const endDate = new Date(formData.period_end)

        if (startDate >= endDate) {
            setError('Period start must be before period end')
            return
        }

        try {
            setLoading(true)
            setError(null)

            let response
            if (isEdit && id) {
                const updateData: UpdatePayoutBatchData = {
                    period_start: `${formData.period_start} 00:00:00`,
                    period_end: `${formData.period_end} 23:59:59`,
                    currency: formData.currency,
                }
                response = await payoutService.updatePayoutBatch(parseInt(id), updateData)
            } else {
                // Convert date strings to datetime format for backend
                const createData = {
                    merchant: formData.merchant,
                    submerchant: formData.submerchant || null, // Convert undefined to null
                    period_start: `${formData.period_start} 00:00:00`,
                    period_end: `${formData.period_end} 23:59:59`,
                    currency: formData.currency || 'USD',
                }
                console.log('Creating payout batch with data:', createData)
                response = await payoutService.createPayoutBatch(createData)
            }

            if (response.success) {
                navigate('/admin/payout-batches')
            } else {
                setError('Failed to save payout batch')
            }
        } catch (err: any) {
            // Try to extract error message from backend response
            console.error('Full error object:', err)
            console.error('Error response:', err.response)
            console.error('Error response data:', err.response?.data)

            let errorMessage = 'An error occurred while saving the payout batch'

            if (err.response?.data) {
                // Check if it's HTML (server error page)
                if (typeof err.response.data === 'string' && err.response.data.includes('<!DOCTYPE html>')) {
                    errorMessage = 'Server error occurred. Please check the console for details.'
                } else if (err.response.data.error) {
                    errorMessage = err.response.data.error
                } else if (err.response.data.message) {
                    errorMessage = err.response.data.message
                } else if (typeof err.response.data === 'object') {
                    errorMessage = JSON.stringify(err.response.data)
                }
            } else if (err.message) {
                errorMessage = err.message
            }

            setError(errorMessage)
        } finally {
            setLoading(false)
        }
    }

    const validateDates = (startDate: string, endDate: string) => {
        if (!startDate || !endDate) {
            setDateError(null)
            return true
        }

        const start = new Date(startDate)
        const end = new Date(endDate)

        if (start >= end) {
            setDateError('Period start must be before period end')
            return false
        } else {
            setDateError(null)
            return true
        }
    }

    const handleInputChange = (field: keyof CreatePayoutBatchData, value: string | number | undefined) => {
        const newFormData = {
            ...formData,
            [field]: value
        }

        setFormData(newFormData)

        // Validate dates when either date field changes
        if (field === 'period_start' || field === 'period_end') {
            validateDates(
                field === 'period_start' ? value as string : formData.period_start,
                field === 'period_end' ? value as string : formData.period_end
            )
        }
    }

    if (loading && isEdit) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={pageStyles.container}>
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: branding.primaryColor }} />
                    <p style={pageStyles.textSecondary}>Loading payout batch...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen" style={pageStyles.container}>
            <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center space-x-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/admin/payout-batches')}
                            style={{ color: branding.textSecondaryColor }}
                            className="hover:text-white"
                        >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                        </Button>
                        <div>
                            <h1 className="text-3xl font-bold" style={pageStyles.text}>
                                {isEdit ? 'Edit Payout Batch' : 'Create Payout Batch'}
                            </h1>
                            <p className="mt-1" style={pageStyles.textSecondary}>
                                {isEdit ? 'Update payout batch details' : 'Create a new payout batch for a merchant'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="rounded-lg p-4 mb-6" style={{
                        backgroundColor: statusColors.error.backgroundColor,
                        borderColor: statusColors.error.borderColor,
                        border: '1px solid'
                    }}>
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 mr-2" style={{ color: statusColors.error.color }} />
                            <p style={{ color: statusColors.error.color }}>{error}</p>
                        </div>
                    </div>
                )}

                {/* Date Validation Error */}
                {dateError && (
                    <div className="rounded-lg p-4 mb-6" style={{
                        backgroundColor: statusColors.warning.backgroundColor,
                        borderColor: statusColors.warning.borderColor,
                        border: '1px solid'
                    }}>
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 mr-2" style={{ color: statusColors.warning.color }} />
                            <p style={{ color: statusColors.warning.color }}>{dateError}</p>
                        </div>
                    </div>
                )}

                {/* Form */}
                <div className="border backdrop-blur-md shadow-sm p-6" style={cardStyles}>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Merchant Selection */}
                            <div>
                                <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                    Merchant * (Only enabled merchants shown)
                                </label>
                                <div className="relative">
                                    <div className="p-1 rounded absolute left-3 top-1/2 transform -translate-y-1/2" style={{ backgroundColor: `${branding.primaryColor}20` }}>
                                        <Building className="h-4 w-4" style={{ color: branding.primaryColor }} />
                                    </div>
                                    <select
                                        value={formData.merchant}
                                        onChange={(e) => handleInputChange('merchant', parseInt(e.target.value))}
                                        className="w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                                        style={{
                                            backgroundColor: branding.surfaceColor,
                                            borderColor: `${branding.textSecondaryColor}30`,
                                            color: branding.textColor
                                        }}
                                        required
                                        disabled={isEdit} // Don't allow changing merchant in edit mode
                                    >
                                        <option value={0}>Select a merchant</option>
                                        {merchants.map((merchant) => (
                                            <option key={merchant.id} value={merchant.id}>
                                                {merchant.company_name} ({merchant.name})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Submerchant Selection */}
                            <div>
                                <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                    Submerchant (Optional)
                                </label>
                                <select
                                    value={formData.submerchant || ''}
                                    onChange={(e) => handleInputChange('submerchant', e.target.value ? parseInt(e.target.value) : undefined)}
                                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                                    style={{
                                        backgroundColor: branding.surfaceColor,
                                        borderColor: `${branding.textSecondaryColor}30`,
                                        color: branding.textColor,
                                    }}
                                >
                                    <option value="">No submerchant</option>
                                    {/* This would be populated with actual submerchants */}
                                    <option value={1}>Submerchant 1</option>
                                    <option value={2}>Submerchant 2</option>
                                </select>
                            </div>

                            {/* Period Start */}
                            <div>
                                <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                    Period Start *
                                </label>
                                <div className="relative">
                                    <div className="p-1 rounded absolute left-3 top-1/2 transform -translate-y-1/2" style={{ backgroundColor: `${branding.secondaryColor}20` }}>
                                        <Calendar className="h-4 w-4" style={{ color: branding.secondaryColor }} />
                                    </div>
                                    <Input
                                        type="date"
                                        value={formData.period_start}
                                        onChange={(e) => handleInputChange('period_start', e.target.value)}
                                        className="pl-10 border"
                                        style={{
                                            backgroundColor: branding.surfaceColor,
                                            borderColor: `${branding.textSecondaryColor}30`,
                                            color: branding.textColor
                                        }}
                                        required
                                    />
                                </div>
                                <p className="text-xs mt-1" style={pageStyles.textSecondary}>Start date of the payout period</p>
                            </div>

                            {/* Period End */}
                            <div>
                                <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                    Period End *
                                </label>
                                <div className="relative">
                                    <div className="p-1 rounded absolute left-3 top-1/2 transform -translate-y-1/2" style={{ backgroundColor: `${statusColors.success.backgroundColor}` }}>
                                        <Calendar className="h-4 w-4" style={{ color: statusColors.success.color }} />
                                    </div>
                                    <Input
                                        type="date"
                                        value={formData.period_end}
                                        onChange={(e) => handleInputChange('period_end', e.target.value)}
                                        className="pl-10 border"
                                        style={{
                                            backgroundColor: branding.surfaceColor,
                                            borderColor: `${branding.textSecondaryColor}30`,
                                            color: branding.textColor
                                        }}
                                        required
                                    />
                                </div>
                                <p className="text-xs mt-1" style={pageStyles.textSecondary}>End date of the payout period (must be after start date)</p>
                            </div>

                            {/* Currency */}
                            <div>
                                <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>
                                    Currency
                                </label>
                                <select
                                    value={formData.currency}
                                    onChange={(e) => handleInputChange('currency', e.target.value)}
                                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                                    style={{
                                        backgroundColor: branding.surfaceColor,
                                        borderColor: `${branding.textSecondaryColor}30`,
                                        color: branding.textColor,
                                    }}
                                >
                                    <option value="USD">USD - US Dollar</option>
                                    <option value="EUR">EUR - Euro</option>
                                    <option value="GBP">GBP - British Pound</option>
                                    <option value="CAD">CAD - Canadian Dollar</option>
                                </select>
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className="flex items-center justify-end space-x-4 pt-6 border-t" style={{ borderColor: `${branding.textSecondaryColor}30` }}>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate('/admin/payout-batches')}
                                disabled={loading}
                                style={{
                                    color: branding.textSecondaryColor,
                                    borderColor: `${branding.textSecondaryColor}40`,
                                    backgroundColor: 'transparent'
                                }}
                                className="hover:bg-gray-800 hover:text-white"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={loading || !!dateError}
                                className="flex items-center text-white"
                                style={pageStyles.primaryBg}
                            >
                                {loading ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : isEdit ? (
                                    <Save className="h-4 w-4 mr-2" />
                                ) : (
                                    <Plus className="h-4 w-4 mr-2" />
                                )}
                                {isEdit ? 'Update Batch' : 'Create Batch'}
                            </Button>
                        </div>
                    </form>
                </div>

                {/* Help Text */}
                <div className="mt-6 rounded-lg p-4" style={{
                    backgroundColor: `${branding.primaryColor}20`,
                    borderColor: `${branding.primaryColor}40`,
                    border: '1px solid'
                }}>
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <Receipt className="h-5 w-5" style={{ color: branding.primaryColor }} />
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium" style={{ color: branding.primaryColor }}>Creating a Payout Batch</h3>
                            <div className="mt-2 text-sm" style={{ color: `${branding.primaryColor}CC` }}>
                                <ul className="list-disc list-inside space-y-1">
                                    <li>Select the merchant and time period for the payout batch</li>
                                    <li>The system will automatically calculate commissions and totals</li>
                                    <li>After creation, you can review and approve the batch before marking it as paid</li>
                                    <li>All transactions within the specified period will be included</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
